<template>
    <div class="step4-images">
        <div class="columns">
            <div class="column is-6">
                <div class="field is-flex is-justify-content-center is-align-items-center is-flex-direction-column">
                    <label class="label">{{ translate("wizard.step4.logo_label") }}</label>
                    <div class="image-upload-area image-upload-area--compact" @click="logoInput?.click()">
                        <img
                            v-if="store.logoPreviewUrl"
                            :src="store.logoPreviewUrl"
                            class="image-preview"
                            :alt="translate('wizard.step4.logo_preview_alt')"
                        />
                        <div v-else class="image-placeholder">
                            <i class="fas fa-image mb-1"></i>
                            <p>{{ translate("wizard.step4.logo_click_to_upload") }}</p>
                            <p class="help">{{ translate("wizard.step4.logo_size_hint") }}</p>
                        </div>
                    </div>
                    <input ref="logoInput" type="file" accept="image/*" class="is-hidden" @change="onLogoChange" />
                    <button v-if="store.logoFile" class="button is-small mt-2" @click="clearLogo">
                        <i class="fas fa-times mr-1"></i> {{ translate("wizard.step4.remove_image") }}
                    </button>
                </div>
            </div>

            <div class="column is-6">
                <div class="field is-flex is-justify-content-center is-align-items-center is-flex-direction-column">
                    <label class="label">{{ translate("wizard.step4.banner_label") }}</label>
                    <div class="image-upload-area image-upload-area--compact image-upload-area--wide" @click="backgroundInput?.click()">
                        <img
                            v-if="store.backgroundPreviewUrl"
                            :src="store.backgroundPreviewUrl"
                            class="image-preview"
                            :alt="translate('wizard.step4.banner_preview_alt')"
                        />
                        <div v-else class="image-placeholder">
                            <i class="fas fa-panorama mb-1"></i>
                            <p>{{ translate("page.admin_edit_event.click_to_upload_background") }}</p>
                            <p class="help">{{ translate("page.admin_edit_event.background_image_guidance") }}</p>
                        </div>
                    </div>
                    <input ref="backgroundInput" type="file" accept="image/*" class="is-hidden" @change="onBackgroundChange" />
                    <button v-if="store.backgroundFile" class="button is-small mt-2" @click="clearBackground">
                        <i class="fas fa-times mr-1"></i> {{ translate("wizard.step4.remove_image") }}
                    </button>
                </div>
            </div>
        </div>

        <div class="field mt-5">
            <div class="buttons">
                <button class="button" @click="emit('back')"><i class="fas fa-arrow-left mr-2"></i> {{ translate("common.back") }}</button>
                <button class="button is-primary is-flex-grow-1" data-testid="wizard-continue" @click="emit('continue')">
                    {{ translate("common.continue") }} <i class="fas fa-arrow-right ml-2"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, type Ref } from "vue";
    import { translate } from "../../../Common/translations";
    import { errorToast } from "../../../Common/toasts";
    import {
        hasValidFileSize,
        isValidImageFormat,
        validateImageDimensions,
        MAX_IMAGE_DIMENSION_PX,
        MAX_IMAGE_SIZE_MB,
    } from "../../../Common/imageValidation";
    import { useWizardStore } from "../wizardStore";

    const emit = defineEmits<{
        continue: [];
        back: [];
    }>();

    const store = useWizardStore();
    const logoInput = ref<HTMLInputElement | null>(null);
    const backgroundInput = ref<HTMLInputElement | null>(null);

    // Incremented on every new selection/clear so a validation promise that resolves after the
    // selection changed (e.g. the file was removed, or a different file was picked) can detect
    // it's stale and avoid staging outdated state.
    const logoGeneration = ref(0);
    const backgroundGeneration = ref(0);

    function validateAndStage(file: File, input: HTMLInputElement | null, generation: Ref<number>, onValid: (file: File) => void) {
        const currentGeneration = ++generation.value;

        if (!hasValidFileSize(file, MAX_IMAGE_SIZE_MB)) {
            errorToast(translate("page.admin_edit_event.invalid_image_size"));
            if (input) {
                input.value = "";
            }
            return;
        }
        if (!isValidImageFormat(file)) {
            errorToast(translate("page.admin_edit_event.invalid_image_type"));
            if (input) {
                input.value = "";
            }
            return;
        }

        return validateImageDimensions(file, MAX_IMAGE_DIMENSION_PX).then(dimensionValidation => {
            if (generation.value !== currentGeneration) {
                return;
            }
            if (!dimensionValidation.valid) {
                errorToast(dimensionValidation.error!);
                if (input) {
                    input.value = "";
                }
                return;
            }
            onValid(file);
        });
    }

    function onLogoChange(e: Event) {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) {
            return;
        }
        validateAndStage(file, logoInput.value, logoGeneration, validFile => {
            store.logoFile = validFile;
            store.logoPreviewUrl = URL.createObjectURL(validFile);
        });
    }

    function onBackgroundChange(e: Event) {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) {
            return;
        }
        validateAndStage(file, backgroundInput.value, backgroundGeneration, validFile => {
            store.backgroundFile = validFile;
            store.backgroundPreviewUrl = URL.createObjectURL(validFile);
        });
    }

    function clearLogo() {
        logoGeneration.value++;
        store.logoFile = null;
        store.logoPreviewUrl = null;
        if (logoInput.value) {
            logoInput.value.value = "";
        }
    }

    function clearBackground() {
        backgroundGeneration.value++;
        store.backgroundFile = null;
        store.backgroundPreviewUrl = null;
        if (backgroundInput.value) {
            backgroundInput.value.value = "";
        }
    }
</script>
